import { useState } from 'react';
import { Trans } from 'react-i18next';
import { OnSubmitType } from 'questionFlow/types';
import QuestionOrganism from 'questionFlow/QuestionOrganism';
import { Checkbox, Field } from 'components';
import { useTranslatedValues } from 'hooks/useTranslatedValues';
import { ONFIDO_FACIAL_SCAN_POLICY_URL, ONFIDO_TERMS_OF_SERVICE_URL } from 'services/onfidoSdk';
import { PRIVACY_POLICY_URL } from 'services/environmentVariables';

const cmsLocation = '/questions/identity/onfidoBiometricConsent';

const translations = {
  title: `${cmsLocation}.title`,
  consentLabel: `${cmsLocation}.consentLabel`,
  buttonText: `${cmsLocation}.buttonText`,
  errorRequired: `${cmsLocation}.errorRequired`,
} as const;

export const CONSENT_CHECKBOX_ID = 'onfido-biometric-consent-checkbox';

const policyLink = (href: string) => (
  // eslint-disable-next-line jsx-a11y/anchor-has-content
  <a target="_blank" rel="noreferrer" href={href} className="text-blue-3 underline" />
);

interface OnfidoBiometricConsentProps {
  onSubmit: OnSubmitType;
  onBack: () => void;
  index: number;
  sectionName: string;
  data?: unknown;
  values?: unknown;
}

const OnfidoBiometricConsent = ({ data, values, ...props }: OnfidoBiometricConsentProps) => {
  const content = useTranslatedValues(translations);
  const [checked, setChecked] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (event: React.FormEvent<HTMLInputElement>) => {
    setChecked(event.currentTarget.checked);
    if (event.currentTarget.checked) {
      setError('');
    }
  };

  const handleSubmit = () => {
    if (!checked) {
      setError(content.errorRequired);
      return;
    }
    props.onSubmit({ key: 'onfidoBiometricConsent', value: true });
  };

  return (
    <QuestionOrganism
      {...props}
      onSubmit={handleSubmit}
      title={content.title}
      subtitle={
        <Trans
          i18nKey={`${cmsLocation}.subtitle` as any}
          components={{
            privacy: policyLink(PRIVACY_POLICY_URL),
            facialScan: policyLink(ONFIDO_FACIAL_SCAN_POLICY_URL),
            terms: policyLink(ONFIDO_TERMS_OF_SERVICE_URL),
          }}
        />
      }
      data-testid="onfidoBiometricConsent"
      submitButtonText={content.buttonText}
    >
      <Field data-testid="onfido-biometric-consent" validationError={error} className="ml-9">
        <label className="text-sm flex" htmlFor={CONSENT_CHECKBOX_ID}>
          <Checkbox
            id={CONSENT_CHECKBOX_ID}
            className="-ml-9"
            checked={checked}
            onChange={handleChange}
            color="blue"
          />
          <span className="ml-4">{content.consentLabel}</span>
        </label>
      </Field>
    </QuestionOrganism>
  );
};

export default OnfidoBiometricConsent;
